import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  Users, Target, Mail, CheckCircle, ArrowRight, ArrowLeft,
  Upload, Plus, Sparkles, Rocket
} from "lucide-react";

const CRMOnboardingWizard = ({ onComplete }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [contacts, setContacts] = useState([
    { name: "", email: "", phone: "", tags: "" }
  ]);
  const [pipeline, setPipeline] = useState({
    name: "Sales Pipeline",
    stages: ["Lead", "Contacted", "Proposal", "Negotiation", "Won"]
  });
  const [campaign, setCampaign] = useState({
    name: "Welcome Campaign",
    subject: "Welcome to our business!",
    content: ""
  });
  const [saving, setSaving] = useState(false);

  const totalSteps = 3;

  const handleAddContact = () => {
    setContacts(prev => [...prev, { name: "", email: "", phone: "", tags: "" }]);
  };

  const handleRemoveContact = (idx) => {
    setContacts(prev => prev.filter((_, i) => i !== idx));
  };

  const handleContactChange = (idx, field, value) => {
    setContacts(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleAddStage = () => {
    setPipeline(prev => ({ ...prev, stages: [...prev.stages, ""] }));
  };

  const handleRemoveStage = (idx) => {
    setPipeline(prev => ({ ...prev, stages: prev.stages.filter((_, i) => i !== idx) }));
  };

  const handleStageChange = (idx, value) => {
    setPipeline(prev => {
      const stages = [...prev.stages];
      stages[idx] = value;
      return { ...prev, stages };
    });
  };

  const handleSaveAndComplete = async () => {
    setSaving(true);
    try {
      // Save contacts
      const validContacts = contacts.filter(c => c.name && c.email);
      if (validContacts.length > 0) {
        await fetch(`${API}/crm/onboarding/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
          body: JSON.stringify({ contacts: validContacts })
        });
      }

      // Save pipeline
      if (pipeline.name && pipeline.stages.filter(Boolean).length > 0) {
        await fetch(`${API}/crm/onboarding/pipeline`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
          body: JSON.stringify(pipeline)
        });
      }

      // Save campaign draft
      if (campaign.name && campaign.subject) {
        await fetch(`${API}/crm/onboarding/campaign`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
          body: JSON.stringify(campaign)
        });
      }

      toast.success("CRM setup complete! Welcome aboard.");
      onComplete?.();
    } catch {
      toast.error("Failed to save. You can set these up later from the CRM dashboard.");
      onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="flex items-center justify-between mb-10">
          {[
            { num: 1, label: "Import Contacts", icon: Users },
            { num: 2, label: "Create Pipeline", icon: Target },
            { num: 3, label: "First Campaign", icon: Mail }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  step > s.num ? "bg-emerald-500 text-white" :
                  step === s.num ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30" :
                  "bg-slate-200 text-slate-500"
                }`}>
                  {step > s.num ? <CheckCircle className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                <span className={`text-xs mt-2 font-medium ${step >= s.num ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {idx < 2 && (
                <div className={`w-24 h-0.5 mx-2 mt-[-12px] ${step > s.num ? "bg-emerald-500" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Import Contacts */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid="crm-step-contacts">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-slate-900">Import Your Contacts</h2>
                <p className="text-sm text-slate-500">Add your first customers or leads to get started</p>
              </div>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
              {contacts.map((contact, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <Input placeholder="Name" value={contact.name} onChange={(e) => handleContactChange(idx, "name", e.target.value)} className="col-span-3" />
                  <Input placeholder="Email" value={contact.email} onChange={(e) => handleContactChange(idx, "email", e.target.value)} className="col-span-3" />
                  <Input placeholder="Phone" value={contact.phone} onChange={(e) => handleContactChange(idx, "phone", e.target.value)} className="col-span-3" />
                  <Input placeholder="Tags (comma)" value={contact.tags} onChange={(e) => handleContactChange(idx, "tags", e.target.value)} className="col-span-2" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveContact(idx)} className="col-span-1 text-red-500 hover:text-red-700" disabled={contacts.length === 1}>×</Button>
                </div>
              ))}
            </div>

            <Button type="button" variant="outline" size="sm" onClick={handleAddContact} className="mt-3">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Contact
            </Button>

            <p className="text-xs text-slate-400 mt-4">You can always add more contacts later from the CRM dashboard.</p>
          </div>
        )}

        {/* Step 2: Create Pipeline */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid="crm-step-pipeline">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Target className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-slate-900">Set Up Your Pipeline</h2>
                <p className="text-sm text-slate-500">Define the stages your deals go through</p>
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-slate-700">Pipeline Name</Label>
              <Input value={pipeline.name} onChange={(e) => setPipeline(p => ({...p, name: e.target.value}))} className="mt-1" />
            </div>

            <Label className="text-slate-700">Stages (drag to reorder)</Label>
            <div className="space-y-2 mt-2">
              {pipeline.stages.map((stage, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <Input value={stage} onChange={(e) => handleStageChange(idx, e.target.value)} placeholder="Stage name" className="flex-1" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveStage(idx)} className="text-red-500 hover:text-red-700" disabled={pipeline.stages.length <= 2}>×</Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleAddStage} className="mt-3">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Stage
            </Button>
          </div>
        )}

        {/* Step 3: First Campaign */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid="crm-step-campaign">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-slate-900">Create Your First Campaign</h2>
                <p className="text-sm text-slate-500">Draft a welcome email for your customers</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Campaign Name</Label>
                <Input value={campaign.name} onChange={(e) => setCampaign(p => ({...p, name: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-slate-700">Email Subject</Label>
                <Input value={campaign.subject} onChange={(e) => setCampaign(p => ({...p, subject: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-slate-700">Email Content</Label>
                <Textarea value={campaign.content} onChange={(e) => setCampaign(p => ({...p, content: e.target.value}))} placeholder="Write your welcome message here..." className="mt-1 h-32" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 p-3 bg-indigo-50 rounded-lg">
              <Sparkles className="h-4 w-4 text-indigo-500" />
              <p className="text-xs text-indigo-700">You can enhance this campaign with AI later from the CRM dashboard.</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="text-slate-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onComplete} className="text-slate-500 text-sm">
              Skip for now
            </Button>
            {step < totalSteps ? (
              <Button onClick={() => setStep(s => s + 1)} className="bg-indigo-600 hover:bg-indigo-700">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSaveAndComplete} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? "Saving..." : (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMOnboardingWizard;
